/**
 * Barras duales por fila (dos métricas de distinta unidad, escala independiente).
 * Pensado para "Inversión y leads (diario)": inversión + leads + CPL del día.
 *
 * @param {{
 *   rows: Array<{ label:string, spend:number|null, leads:number|null, cpl:number|null }>,
 *   emptyText?: string
 * }} props
 */
import { formatAnalyticsNumber } from '../utils/format-number.js';

export default function DualBarList({ rows, emptyText = 'Sin datos para graficar.' }) {
  const list = (Array.isArray(rows) ? rows : []).filter((r) => r && r.label);
  if (!list.length) {
    return <p className="analytics-empty">{emptyText}</p>;
  }
  const spendVals = list
    .map((r) => Number(r.spend))
    .filter((n) => Number.isFinite(n) && n >= 0);
  const leadVals = list
    .map((r) => Number(r.leads))
    .filter((n) => Number.isFinite(n) && n >= 0);
  if (!spendVals.length && !leadVals.length) {
    return <p className="analytics-empty">{emptyText}</p>;
  }
  const maxSpend = Math.max(...spendVals, 1);
  const maxLeads = Math.max(...leadVals, 1);

  return (
    <div className="analytics-dual-list" role="img" aria-label="Inversión y leads por día">
      <div className="analytics-dual-legend">
        <span className="analytics-dual-key">
          <i className="analytics-dual-swatch is-spend" /> Inversión
        </span>
        <span className="analytics-dual-key">
          <i className="analytics-dual-swatch is-leads" /> Leads
        </span>
        <span className="analytics-dual-key analytics-dual-key-cpl">CPL = inversión ÷ leads</span>
      </div>
      {list.map((r) => {
        const spend = Number(r.spend);
        const leads = Number(r.leads);
        const okSpend = Number.isFinite(spend) && spend >= 0;
        const okLeads = Number.isFinite(leads) && leads >= 0;
        const spendPct = okSpend ? Math.round((spend / maxSpend) * 100) : 0;
        const leadsPct = okLeads ? Math.round((leads / maxLeads) * 100) : 0;
        const cpl = r.cpl != null && Number.isFinite(Number(r.cpl)) ? Number(r.cpl) : null;
        return (
          <div key={r.label} className="analytics-dual-row">
            <span className="analytics-dual-label">{r.label}</span>
            <div className="analytics-dual-bars">
              <div className="analytics-dual-track">
                <div
                  className="analytics-dual-fill is-spend"
                  style={{ width: spendPct + '%' }}
                />
                <span className="analytics-dual-num">
                  {okSpend ? formatAnalyticsNumber(spend) : '—'}
                </span>
              </div>
              <div className="analytics-dual-track">
                <div
                  className="analytics-dual-fill is-leads"
                  style={{ width: leadsPct + '%' }}
                />
                <span className="analytics-dual-num">
                  {okLeads ? formatAnalyticsNumber(leads) : '—'}
                </span>
              </div>
            </div>
            <span className="analytics-dual-cpl">
              {cpl != null ? 'CPL ' + formatAnalyticsNumber(cpl) : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}
